import React, { useContext, useState } from "react";
import { Box, Typography, Avatar, Button, Chip } from "@mui/material";
import { styled, keyframes } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import { PerformerContext, filterPerformers } from "../contexts/PerformerContext";
import { PerformerAWContext, filterPerformersAW } from "../contexts/PerformerAWContext";
import { SinPartyContext, filterSinParty } from "../contexts/SinPartyContext";
import { StripchatContext, filterStripchat } from "../contexts/StripchatContext";
import { buildStripchatClickUrl } from "../utils/stripchatP1";
import ChatBot from './ChatBot';
import { PreRollAd } from './TwinredPreRoll';
import RightSidebar from './RightSidebar';
import CamEmbedModal from './CamEmbedModal';
import { Link, useNavigate } from 'react-router-dom';

const blink = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const LiveDot = styled('span')({
  height: '8px', width: '8px',
  backgroundColor: '#4CAF50', borderRadius: '50%',
  display: 'inline-block', marginRight: '8px',
  animation: `${blink} 1.5s ease-in-out infinite`,
});

const LiveText = styled(Box)({
  display: 'flex', alignItems: 'center',
  color: '#4CAF50', fontWeight: 'bold',
});

const ChatButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B6B, #FF8E53, #FF2BAA)',
  borderRadius: '50px', color: 'white', padding: '8px 20px',
  textTransform: 'uppercase', fontWeight: 'bold',
  display: 'flex', alignItems: 'center', gap: '8px',
  minWidth: 'fit-content', whiteSpace: 'nowrap',
  '&:hover': { background: 'linear-gradient(45deg, #FF5252, #FF7043, #FF1A8C)' },
}));

const WatchButton = styled(Button)(() => ({
  background: 'linear-gradient(45deg, #e91e63, #9c27b0)',
  borderRadius: '50px', color: 'white', padding: '8px 20px',
  textTransform: 'uppercase', fontWeight: 'bold',
  display: 'flex', alignItems: 'center', gap: '6px',
  minWidth: 'fit-content', whiteSpace: 'nowrap', marginLeft: '8px',
  '&:hover': { background: 'linear-gradient(45deg, #c2185b, #7b1fa2)' },
}));



// Gallery carousel — swipes through performer's photos
const GalleryCarousel = ({ gallery, displayName, onClick }) => {
  const [idx, setIdx] = useState(0);
  const images = gallery && gallery.length > 0 ? gallery : null;
  if (!images) return null;

  return (
    <Box sx={{ position: "relative", height: { xs: 280, md: 420 }, width: "100%", overflow: "hidden", cursor: "pointer" }}
      onClick={onClick}
    >
      <Box
        component="img"
        src={images[idx].full}
        alt={`${displayName} photo ${idx + 1}`}
        sx={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      {images.length > 1 && (
        <>
          <Box sx={{
            position: "absolute", bottom: 8, left: 0, right: 0,
            display: "flex", justifyContent: "center", gap: 0.5,
          }}>
            {images.map((_, i) => (
              <Box key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                sx={{ width: 6, height: 6, borderRadius: "50%", cursor: "pointer",
                  bgcolor: i === idx ? "#FF4196" : "rgba(255,255,255,0.6)" }}
              />
            ))}
          </Box>
          <Box onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + images.length) % images.length); }}
            sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.4)", color: "#fff", borderRadius: "50%",
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >‹</Box>
          <Box onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % images.length); }}
            sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              bgcolor: "rgba(0,0,0,0.4)", color: "#fff", borderRadius: "50%",
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >›</Box>
        </>
      )}
    </Box>
  );
};

// Mobile ad banner — 300x250 Twinred, injected between performer cards
let adScriptInjected = false;
const MobileAdBanner = () => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current) return;
    // Create ins tag
    const ins = document.createElement('ins');
    ins.setAttribute('data-tr-zone', '01DXF6DT004000000000002PRX');
    const sc = document.createElement('script');
    sc.type = 'text/javascript';
    sc.async = true;
    sc.src = 'https://s.ad.twinrdengine.com/adlib.js';
    ins.appendChild(sc);
    ref.current.appendChild(ins);
  }, []);
  return (
    <Box sx={{
      display: { xs: 'flex', md: 'none' },
      justifyContent: 'center', alignItems: 'center',
      width: '100%', minHeight: 260, my: 1,
      bgcolor: 'transparent'
    }}>
      <div ref={ref} style={{ width: 300, minHeight: 250 }} />
    </Box>
  );
};


const PerformerCard = () => {
  const { performers, loading, selectedFilters, ratings, apiSearchResults, apiSearchLoading } = useContext(PerformerContext);
  const { performers: performersAW, loading: loadingAW } = useContext(PerformerAWContext);
  const { performers: performersSP, loading: loadingSP } = useContext(SinPartyContext);
  const { performers: performersSC, loading: loadingSC } = useContext(StripchatContext);
  const [activeChatPerformer, setActiveChatPerformer]   = useState(null);
  const [activeEmbedPerformer, setActiveEmbedPerformer] = useState(null);
  const [preRollPending, setPreRollPending] = useState(null);
  const navigate = useNavigate();

  if (loading || loadingAW || loadingSP || loadingSC) return <Typography>Loading performers...</Typography>;

  const filteredPerformers   = filterPerformers(performers, selectedFilters || {});
  const filteredPerformersAW = filterPerformersAW(performersAW, selectedFilters || {});
  const filteredPerformersSP = filterSinParty(performersSP, selectedFilters || {});
  const filteredPerformersSC = filterStripchat(performersSC, selectedFilters || {});

  // If local results are thin (<3), fall back to API search results
  const hasActiveFilter = (selectedFilters?.searchTerm || '').trim().length >= 2 ||
    (selectedFilters?.ethnicity || []).length > 0 ||
    (selectedFilters?.hairColor || []).length > 0 ||
    (selectedFilters?.age || []).length > 0;
  const useApiResults = hasActiveFilter && filteredPerformers.length < 3 && !apiSearchLoading && apiSearchResults.length > 0;

  // Show loading state while API search is in progress
  if (hasActiveFilter && apiSearchLoading && filteredPerformers.length < 3) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
        <Typography sx={{ color: '#FF4196', fontWeight: 700 }}>🔍 Searching live models...</Typography>
      </Box>
    );
  }

  // No results at all
  if (hasActiveFilter && !apiSearchLoading && filteredPerformers.length === 0 && apiSearchResults.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
        <Typography sx={{ fontSize: 40 }}>😔</Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>No live models found</Typography>
        <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>Try different keywords or browse categories</Typography>
      </Box>
    );
  }

  const displayPerformers = useApiResults ? apiSearchResults : filteredPerformers;

  // ── Strict round-robin: 1-Cambuilder 2-AWEmpire 3-SinParty 4-StripCash ──
  const allPerformers = (() => {
    if (useApiResults) return displayPerformers;
    const sources = [
      displayPerformers,       // 1 — Cambuilder/Streamate
      filteredPerformersAW,    // 2 — AWEmpire/LiveJasmin
      filteredPerformersSP,    // 3 — SinParty
      filteredPerformersSC,    // 4 — StripCash/Stripchat
    ];
    const maxLen = Math.max(...sources.map(s => s.length));
    const result = [];
    for (let i = 0; i < maxLen; i++) {
      sources.forEach(src => {
        if (src[i]) result.push(src[i]);
      });
    }
    return result;
  })();

  const handleChatOpen = (performer) => {
    window.gtag?.('event', 'chatbot', {
      event_category: 'engagement',
      event_label: `Chat with ${performer.displayName}`,
    });
    setActiveChatPerformer(performer);
  };

  const handleWatchLive = (performer) => {
    window.gtag?.('event', 'watch_live', {
      event_category: 'engagement',
      event_label: performer.displayName,
    });
    // LiveJasmin / SinParty — open in new tab
    const isLJ = filteredPerformersAW.some(p => p.uniqueModelId === performer.uniqueModelId);
    const isSP = performer.source === 'sinparty';
    const isSC = performer.source === 'stripchat';
    if (isLJ || isSP || isSC) {
      const url = isSC
        ? buildStripchatClickUrl(performer.chatRoomUrl, 'homepage')
        : performer.chatRoomUrl;
      window.open(url, '_blank');
      return;
    }
    setPreRollPending(performer);
  };

  const isStreamatePerformer = (performer) =>
    displayPerformers.some(p => p.uniqueModelId === performer.uniqueModelId);

  return (
    <>
      {preRollPending && (
        <PreRollAd onComplete={() => {
          // After pre-roll, open Streamate directly — embed iframe has sizing issues
          const p = preRollPending;
          const url = p.chatRoomUrl || `https://instacamgirlslive.com/cam/${encodeURIComponent(p.displayName || p.name)}/?AFNO=1-94033`;
          window.open(url, '_blank');
          setPreRollPending(null);
        }} />
      )}

      {/* ── MOBILE: 2-column compact grid ── */}
      <Box sx={{ display: { xs: 'grid', md: 'none' }, gridTemplateColumns: '1fr 1fr', gap: '10px', width: '100%', px: 0.5 }}>
        {allPerformers.flatMap((performer, index) => {
          const mobileCard = (
            <Box key={performer.uniqueModelId}
              onClick={() => navigate(`/cam/${performer.displayName}`, { state: { performer } })}
              sx={{ borderRadius: '10px', overflow: 'hidden', backgroundColor: '#111', cursor: 'pointer', position: 'relative' }}
            >
              {/* Thumbnail */}
              <div style={{ width: '100%', paddingBottom: '133%', position: 'relative', backgroundColor: '#222' }}>
                <img
                  src={performer.profilePictureFullUrl || performer.profilePictureUrl}
                  alt={performer.displayName}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                  onError={e => {
                    if (e.target.src !== performer.profilePictureUrl && performer.profilePictureUrl) {
                      e.target.src = performer.profilePictureUrl;
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                />
                {/* LIVE badge top-left */}
                <div style={{ position: 'absolute', top: 6, left: 6, background: '#e91e63', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 4, letterSpacing: 0.5 }}>
                  ● LIVE
                </div>
                {/* Gold show badge */}
                {performer.goldShow && (
                  <div style={{ position: 'absolute', top: 6, right: 6, background: '#FFD700', color: '#000', fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>🥇</div>
                )}
                {/* New model badge — SinParty */}
                {performer.isNew && performer.source === 'sinparty' && (
                  <div style={{ position: 'absolute', top: performer.goldShow ? 24 : 6, right: 6, background: 'linear-gradient(135deg,#ff6b35,#e91e63)', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>🔥 NEW</div>
                )}
                {/* Name + Watch gradient overlay */}
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', padding: '18px 8px 8px' }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{performer.displayName}</div>
                  <div style={{ color: '#f48fb1', fontSize: 11, marginTop: 2 }}>
                    {performer.age ? `${performer.age}y` : ''}{performer.ethnicity ? ` · ${performer.ethnicity}` : ''}
                  </div>
                </div>
              </div>
              {/* Buttons row */}
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, background: 'linear-gradient(90deg,#e91e63,#f06292)', color: '#fff', fontWeight: 700, fontSize: 11, textAlign: 'center', padding: '7px 4px', letterSpacing: 0.3 }}>
                  Watch 🔴
                </div>
                <div
                  onClick={ev => { ev.stopPropagation(); handleChatOpen(performer); }}
                  style={{ flex: 1, background: 'linear-gradient(90deg,#7c3aed,#a855f7)', color: '#fff', fontWeight: 700, fontSize: 11, textAlign: 'center', padding: '7px 4px', letterSpacing: 0.3 }}
                >
                  Chat 💬
                </div>
              </div>
            </Box>
          );
          const items = [mobileCard];
          // TwinRed ad every 6 models
          if ((index + 1) % 6 === 0) {
            items.push(
              <Box key={`ad-${index}`} sx={{ gridColumn: '1 / -1' }}>
                <MobileAdBanner />
              </Box>
            );
          }
          // Kwiky clip card every 10 models (mobile only)
          if ((index + 1) % 10 === 0) {
            items.push(
              <Box key={`kwiky-${index}`} sx={{ gridColumn: '1 / -1' }}>
                <a
                  href="https://kwiky.com/landing/click/clip/cams?AFNO=2-162513.49936"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'block', textDecoration: 'none' }}
                >
                  <Box sx={{
                    background: 'linear-gradient(90deg, #ff2d55 0%, #ff6b35 100%)',
                    borderRadius: '12px',
                    px: 2, py: 1.4,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    mx: 0.5,
                  }}>
                    <span style={{ fontSize: 24 }}>🎬</span>
                    <Box sx={{ flex: 1 }}>
                      <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.3 }}>Short Clips → Live Cams</div>
                      <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem' }}>Watch a clip, then go live with her</div>
                    </Box>
                    <span style={{ color: '#fff', fontSize: 20 }}>›</span>
                  </Box>
                </a>
              </Box>
            );
          }
          return items;
        })}
      </Box>

      {/* ── DESKTOP: single-column Instagram-style cards ── */}
      {allPerformers.flatMap((performer, index) => {
        const card = (
        <React.Fragment key={performer.uniqueModelId}>
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            maxWidth: 500, border: "1px solid #dbdbdb", borderRadius: "8px",
            overflow: "hidden", backgroundColor: "#fff", marginBottom: 3,
          }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", padding: "10px" }}>
              <Avatar src={performer.profilePictureUrl} alt={performer.displayName}
                sx={{ width: 40, height: 40, marginRight: 1.5 }} />
              <Box>
                <Typography sx={{ fontWeight: "bold" }} component={Link} to={`/cam/${performer.displayName}`} style={{ textDecoration: "none", color: "inherit" }}>{performer.displayName}</Typography>
                {performer.goldShow && (
                  <Chip label="🥇 GOLD SHOW" size="small"
                    sx={{ bgcolor: '#FFD700', color: '#000', fontSize: '0.65rem', height: 18 }} />
                )}
                {performer.isNew && performer.source === 'sinparty' && (
                  <Chip label="🔥 NEW" size="small"
                    sx={{ background: 'linear-gradient(135deg,#ff6b35,#e91e63)', color: '#fff', fontSize: '0.65rem', height: 18, fontWeight: 700, ml: 0.5 }} />
                )}
              </Box>
              <Box sx={{ flex: 1 }} />
              <LiveText>
                <LiveDot />
                <Typography variant="body2" sx={{ color: 'inherit' }}>Live Now</Typography>
              </LiveText>
            </Box>

            {/* Main image / gallery */}
            {performer.gallery && performer.gallery.length > 0 ? (
              <GalleryCarousel
                gallery={performer.gallery}
                displayName={performer.displayName}
                onClick={() => handleWatchLive(performer)}
              />
            ) : (
              <div style={{ height: '420px', width: '100%', overflow: 'hidden', cursor: 'pointer', backgroundColor: '#222', position: 'relative' }}
                onClick={() => handleWatchLive(performer)}
              >
                <img
                  src={performer.profilePictureFullUrl || performer.profilePictureUrl}
                  alt={performer.displayName}
                  crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
                  onError={(e) => {
                    if (e.target.src !== performer.profilePictureUrl && performer.profilePictureUrl) {
                      e.target.src = performer.profilePictureUrl;
                    }
                  }}
                />
                <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                  LIVE
                </div>
              </div>
            )}

            {/* About / bio */}
            {performer.about ? (
              <Box sx={{ px: 2, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', fontSize: '0.82rem' }}>
                  "{performer.about}"
                </Typography>
              </Box>
            ) : null}

            {/* Footer */}
            <Box sx={{ padding: "10px", width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1 }}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Age: {performer.age}</Typography>
                  {(performer.fetishes?.length > 0) && (
                    <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.78rem', color: '#888' }}>
                      {performer.fetishes.slice(0, 3).map(f => `#${f}`).join(' ')}
                    </Typography>
                  )}
                  {performer.turnOns && (
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#aaa' }}>
                      Turns on: {performer.turnOns.slice(0, 40)}…
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                  <WatchButton variant="contained" onClick={() => handleWatchLive(performer)}>
                    <LiveTvIcon sx={{ fontSize: 16 }} /> Watch Live
                  </WatchButton>
                  <ChatButton variant="contained" onClick={() => handleChatOpen(performer)}>
                    Let's Chat! <FavoriteIcon sx={{ fontSize: 16 }} />
                  </ChatButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </React.Fragment>
        );
        return [card];
      })}

      <ChatBot
        isOpen={activeChatPerformer !== null}
        onClose={() => setActiveChatPerformer(null)}
        performerName={activeChatPerformer?.displayName}
        performer={activeChatPerformer}
      />
    </>
  );
};

export default PerformerCard;
